import React, { useEffect } from "react";
import { createPortal } from "react-dom";

/**
 * TailwindModal - Reusable centered modal with optional backdrop blur
 * Renders via portal to document.body for reliable viewport centering
 *
 * @param {boolean} isOpen - Whether the modal is visible
 * @param {Function} [onClose] - Optional callback when backdrop is clicked
 * @param {ReactNode} children - Modal content
 * @param {string} [contentClassName] - Optional classes for the inner content wrapper
 * @param {boolean} [showBackdrop=true] - When false, transparent overlay (for toasts)
 */
const TailwindModal = ({ isOpen, onClose, children, contentClassName = "", showBackdrop = true }) => {
    useEffect(() => {
        if (isOpen && showBackdrop) {
            document.body.style.overflow = "hidden";
            return () => {
                document.body.style.overflow = "";
            };
        }
    }, [isOpen, showBackdrop]);

    if (!isOpen) return null;

    const overlayClass = showBackdrop
        ? "fixed top-0 left-0 right-0 bottom-0 z-[2000] flex items-center justify-center p-4 bg-black/20 backdrop-blur-sm"
        : "fixed top-0 left-0 right-0 bottom-0 z-[2000] flex items-center justify-center p-4 pointer-events-none";

    const modalContent = (
        <div
            className={overlayClass}
            onClick={showBackdrop ? onClose : undefined}
            role="dialog"
            aria-modal="true"
        >
            <div
                className={`relative ${contentClassName} ${!showBackdrop ? "pointer-events-auto" : ""}`}
                onClick={(e) => e.stopPropagation()}
            >
                {children}
            </div>
        </div>
    );

    return createPortal(modalContent, document.body);
};

export default TailwindModal;
